import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getSession,
  endSession,
  deleteSession,
  getSessionScreenshots,
  uploadScreenshot,
  getImageUrl,
  updateScreenshotMetadata,
  deleteScreenshots,
} from '../../services/api';
import socketService from '../../services/socket';
import { 
  compressImage, 
  getCompressionStats, 
  isCompressionSupported,
  formatFileSize,
  getCompressionProfile
} from '../../utils/imageCompression';
import {
  shouldShowCameraButton,
  captureFromCamera,
  fileToDataURL
} from '../../utils/cameraUtils';
import { shouldUseMobileUI } from '../../utils/deviceDetection';
import { useSettings } from '../../contexts/SettingsContext';
import ScreenshotLightbox from './ScreenshotLightbox';
import MobileNav from '../Mobile/MobileNav';
import FloatingActionButton from '../Mobile/FloatingActionButton';
import './SessionView.css';

function SessionView({ user }) {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [session, setSession] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressionEnabled] = useState(isCompressionSupported());
  const [showCameraButton] = useState(shouldShowCameraButton());
  
  // Mobile UI state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(shouldUseMobileUI());
  
  // Update mobile detection on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(shouldUseMobileUI());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Selection state for multi-select/delete
  const [selectedScreenshots, setSelectedScreenshots] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('screenshot-sort-order') || 'desc';
  });
  const [sortedScreenshots, setSortedScreenshots] = useState([]);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // User filter state
  const [userFilter, setUserFilter] = useState('all'); // 'all', 'me', or userId

  useEffect(() => {
    loadSession();
    loadScreenshots();

    // Join session room
    if (socketService.isConnected()) {
      socketService.joinSession(sessionId);
    }

    // Listen for new screenshots
    const handleScreenshotUploaded = (data) => {
      console.log('New screenshot uploaded:', data);
      loadScreenshots(); // Reload screenshots
    };

    const handleMemberJoined = (data) => {
      console.log('Member joined:', data.username);
    };

    const handleMemberLeft = (data) => {
      console.log('Member left:', data.username);
    };

    socketService.onScreenshotUploaded(handleScreenshotUploaded);
    socketService.onMemberJoined(handleMemberJoined);
    socketService.onMemberLeft(handleMemberLeft);

    // Listen for Electron screenshot captures
    let cleanupScreenshotListener;
    if (window.electronAPI) {
      cleanupScreenshotListener = window.electronAPI.onScreenshotCaptured(async (data) => {
        console.log('Screenshot captured:', data);
        await handleScreenshotCapture(data);
      });
    }

    return () => {
      socketService.leaveSession(sessionId);
      socketService.off('screenshot-uploaded', handleScreenshotUploaded);
      socketService.off('member-joined', handleMemberJoined);
      socketService.off('member-left', handleMemberLeft);
      // Cleanup Electron event listener
      if (cleanupScreenshotListener) {
        cleanupScreenshotListener();
      }
    };
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const data = await getSession(sessionId);
      setSession(data);
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const loadScreenshots = async () => {
    try {
      const data = await getSessionScreenshots(sessionId);
      setScreenshots(data.screenshots || []);
    } catch (err) {
      console.error('Failed to load screenshots:', err);
    }
  };

  // Helper function to get upload time CSS class based on performance
  const getUploadTimeClass = (uploadTimeMs) => {
    if (!uploadTimeMs) return 'upload-time-unknown';
    if (uploadTimeMs < 500) return 'upload-time-excellent';
    if (uploadTimeMs < 1000) return 'upload-time-good';
    if (uploadTimeMs < 2000) return 'upload-time-slow';
    return 'upload-time-very-slow';
  };

  // Helper function to format upload time display
  const formatUploadTime = (uploadTimeMs) => {
    if (!uploadTimeMs) return 'Upload time: Unknown';
    return `⚡ Uploaded in ${uploadTimeMs}ms`;
  };

  // Helper function to get compression profile display name
  const getProfileDisplay = (screenshot) => {
    if (!screenshot.metadata || !screenshot.metadata.compressionProfile) {
      return null;
    }
    return screenshot.metadata.compressionProfile;
  };

  // Sort screenshots by captured date
  const sortScreenshots = (screenshots, order) => {
    return [...screenshots].sort((a, b) => {
      const dateA = new Date(a.capturedAt);
      const dateB = new Date(b.capturedAt);
      return order === 'desc' ? dateB - dateA : dateA - dateB;
    });
  };

  // Update sorted screenshots when screenshots or sort order changes
  useEffect(() => {
    setSortedScreenshots(sortScreenshots(screenshots, sortOrder));
  }, [screenshots, sortOrder]);
  
  // Get unique participants from screenshots
  const getParticipants = () => {
    const uniqueUsers = new Map();
    screenshots.forEach(screenshot => {
      if (!uniqueUsers.has(screenshot.userId)) {
        uniqueUsers.set(screenshot.userId, {
          id: screenshot.userId,
          username: screenshot.username,
          displayName: screenshot.displayName || screenshot.username,
        });
      }
    });
    return Array.from(uniqueUsers.values());
  };
  
  // Filter screenshots based on selected user
  const getFilteredScreenshots = () => {
    if (userFilter === 'all') {
      return sortedScreenshots;
    } else if (userFilter === 'me') {
      return sortedScreenshots.filter(s => s.userId === user.id);
    } else {
      // Filter by specific user ID
      return sortedScreenshots.filter(s => s.userId === userFilter);
    }
  };
  
  const filteredScreenshots = getFilteredScreenshots();

  // Persist sort order to localStorage
  useEffect(() => {
    localStorage.setItem('screenshot-sort-order', sortOrder);
  }, [sortOrder]);

  // Selection handlers
  const handleSelectScreenshot = (screenshotId) => {
    setSelectedScreenshots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(screenshotId)) {
        newSet.delete(screenshotId);
      } else {
        newSet.add(screenshotId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedScreenshots.size === sortedScreenshots.length) {
      setSelectedScreenshots(new Set());
    } else {
      setSelectedScreenshots(new Set(sortedScreenshots.map(s => s.id)));
    }
  };

  const handleDeleteSelected = async () => {
    const count = selectedScreenshots.size;
    console.log('[SessionView] handleDeleteSelected called, count:', count);
    console.log('[SessionView] selectedScreenshots:', Array.from(selectedScreenshots));
    
    if (count === 0) {
      console.log('[SessionView] No screenshots selected, returning');
      return;
    }
    
    if (!window.confirm(`Delete ${count} screenshot${count > 1 ? 's' : ''}? This cannot be undone.`)) {
      console.log('[SessionView] User cancelled delete');
      return;
    }
    
    try {
      console.log('[SessionView] Calling deleteScreenshots API...');
      await deleteScreenshots(Array.from(selectedScreenshots));
      console.log('[SessionView] Delete successful, reloading screenshots...');
      await loadScreenshots();
      setSelectedScreenshots(new Set());
      setIsSelectionMode(false);
      console.log('[SessionView] Delete complete');
    } catch (err) {
      console.error('[SessionView] Failed to delete screenshots:', err);
      setError('Failed to delete screenshots');
    }
  };

  const handleScreenshotClick = (screenshotId) => {
    if (isSelectionMode) return; // Don't open lightbox in selection mode
    const index = filteredScreenshots.findIndex(s => s.id === screenshotId);
    if (index !== -1) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  const handleScreenshotCapture = async (captureData) => {
    const captureStartTime = performance.now();
    const captureTimestamp = new Date().toISOString();
    
    try {
      console.log('[SessionView] Screenshot captured:', {
        filename: captureData.filename,
        originalSize: formatFileSize(captureData.size),
        timestamp: captureData.timestamp,
        captureTimestamp
      });
      
      setUploadProgress(10); // Starting
      
      // Read the file using Electron API
      const readStartTime = performance.now();
      const buffer = await window.electronAPI.readFile(captureData.filepath);
      const blob = new Blob([buffer], { type: 'image/png' });
      const readTime = performance.now() - readStartTime;
      
      setUploadProgress(20); // File read complete
      
      let fileToUpload;
      let filename = captureData.filename;
      let compressionTime = 0;
      let profileUsed = 'none';
      
      // Try to compress image if supported
      if (compressionEnabled) {
        try {
          console.log('[SessionView] Compressing image...');
          const compressionStartTime = performance.now();
          
          // Get active compression profile from settings
          const profileId = settings.compression.activeProfile;
          const customSettings = profileId === 'custom' ? settings.compression.customProfile : {};
          const profile = getCompressionProfile(profileId, customSettings);
          
          profileUsed = profile.name || profileId;
          console.log('[SessionView] Using compression profile:', profileUsed);
          
          const compressedBlob = await compressImage(blob, {
            quality: profile.quality,
            maxWidth: profile.maxWidth,
            maxHeight: profile.maxHeight,
          });
          
          compressionTime = performance.now() - compressionStartTime;
          
          // Log compression stats
          const stats = getCompressionStats(blob.size, compressedBlob.size);
          console.log('[SessionView] Compression complete:', {
            profile: profileUsed,
            resolution: `${profile.maxWidth}×${profile.maxHeight}`,
            quality: `${Math.round(profile.quality * 100)}%`,
            ...stats,
            compressionTime: `${compressionTime.toFixed(1)}ms`,
          });
          
          // Change extension to .jpg
          filename = captureData.filename.replace(/\.png$/i, '.jpg');
          fileToUpload = new File([compressedBlob], filename, { 
            type: 'image/jpeg' 
          });
          
          setUploadProgress(40); // Compression complete
        } catch (compressionError) {
          console.warn('[SessionView] Compression failed, using original:', compressionError);
          profileUsed = 'failed-fallback-to-png';
          // Fallback to original PNG
          fileToUpload = new File([blob], captureData.filename, { 
            type: 'image/png' 
          });
          setUploadProgress(40);
        }
      } else {
        console.log('[SessionView] Compression not supported, using original PNG');
        profileUsed = 'compression-not-supported';
        fileToUpload = new File([blob], captureData.filename, { 
          type: 'image/png' 
        });
        setUploadProgress(40);
      }
      
      // Upload to server
      console.log('[SessionView] Uploading screenshot:', {
        filename: fileToUpload.name,
        size: formatFileSize(fileToUpload.size),
        type: fileToUpload.type
      });
      
      setUploadProgress(50); // Starting upload
      
      // Measure upload time by wrapping the API call
      const uploadStartTime = performance.now();
      let uploadTime = 0;
      let uploadedScreenshot;
      
      try {
        // Include compression profile info in metadata
        const captureMetadata = {
          compressionProfile: profileUsed,
          compressionTime: Math.round(compressionTime),
          originalFileType: captureData.filename.endsWith('.png') ? 'png' : 'unknown',
          compressedFileType: fileToUpload.type.includes('jpeg') ? 'jpeg' : 'png',
        };
        
        uploadedScreenshot = await uploadScreenshot(
          fileToUpload, 
          sessionId,
          undefined, // positionX
          undefined, // positionY
          undefined, // game
          undefined, // map
          captureMetadata  // metadata with profile info
        );
        uploadTime = performance.now() - uploadStartTime;
        
        // Now update the screenshot with the actual upload time
        await updateScreenshotMetadata(uploadedScreenshot.id, {
          uploadTimeMs: Math.round(uploadTime)
        });
      } catch (uploadError) {
        uploadTime = performance.now() - uploadStartTime;
        throw uploadError; // Re-throw to be caught by outer try-catch
      }
      
      setUploadProgress(80); // Upload complete

      // Notify other users via socket
      if (socketService.isConnected()) {
        socketService.emitScreenshotCaptured(sessionId, uploadedScreenshot.id);
      }

      // Reload screenshots
      await loadScreenshots();
      setUploadProgress(100); // All done
      
      const totalTime = performance.now() - captureStartTime;
      
      // Log comprehensive performance metrics
      console.log('[SessionView] Screenshot upload complete:', {
        screenshotId: uploadedScreenshot.id,
        captureTimestamp,
        profileUsed,
        performanceMetrics: {
          readTime: `${readTime.toFixed(1)}ms`,
          compressionTime: `${compressionTime.toFixed(1)}ms`,
          uploadTime: `${uploadTime.toFixed(1)}ms`,
          totalTime: `${totalTime.toFixed(1)}ms`,
        },
        fileSize: formatFileSize(fileToUpload.size),
      });
      
      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (err) {
      console.error('[SessionView] Failed to upload screenshot:', err);
      setError('Failed to upload screenshot');
      setUploadProgress(0);
    }
  };

  const handleCameraCapture = async (cameraFile) => {
    const captureStartTime = performance.now();
    const captureTimestamp = new Date().toISOString();
    
    try {
      console.log('[SessionView] Camera capture initiated');
      
      setUploadProgress(5); // Starting
      
      // cameraFile is passed as parameter from FloatingActionButton
      if (!cameraFile) {
        throw new Error('No image captured from camera');
      }
      
      console.log('[SessionView] Camera image captured:', {
        filename: cameraFile.name,
        originalSize: formatFileSize(cameraFile.size),
        type: cameraFile.type,
        timestamp: captureTimestamp
      });
      
      setUploadProgress(15); // Camera capture complete
      
      // Convert to blob for processing
      const blob = new Blob([cameraFile], { type: cameraFile.type });
      
      let fileToUpload;
      let filename = `camera-${Date.now()}.jpg`;
      let compressionTime = 0;
      let profileUsed = 'none';
      
      // Try to compress image if supported
      if (compressionEnabled) {
        try {
          console.log('[SessionView] Compressing camera image...');
          const compressionStartTime = performance.now();
          
          // Get active compression profile from settings
          const profileId = settings.compression.activeProfile;
          const customSettings = profileId === 'custom' ? settings.compression.customProfile : {};
          const profile = getCompressionProfile(profileId, customSettings);
          
          profileUsed = profile.name || profileId;
          console.log('[SessionView] Using compression profile:', profileUsed);
          
          const compressedBlob = await compressImage(blob, {
            quality: profile.quality,
            maxWidth: profile.maxWidth,
            maxHeight: profile.maxHeight,
          });
          
          compressionTime = performance.now() - compressionStartTime;
          
          // Log compression stats
          const stats = getCompressionStats(blob.size, compressedBlob.size);
          console.log('[SessionView] Compression complete:', {
            profile: profileUsed,
            resolution: `${profile.maxWidth}×${profile.maxHeight}`,
            quality: `${Math.round(profile.quality * 100)}%`,
            ...stats,
            compressionTime: `${compressionTime.toFixed(1)}ms`,
          });
          
          fileToUpload = new File([compressedBlob], filename, { 
            type: 'image/jpeg' 
          });
          
          setUploadProgress(40); // Compression complete
        } catch (compressionError) {
          console.warn('[SessionView] Compression failed, using original:', compressionError);
          profileUsed = 'failed-fallback-to-original';
          // Fallback to original
          fileToUpload = new File([blob], filename, { 
            type: cameraFile.type 
          });
          setUploadProgress(40);
        }
      } else {
        console.log('[SessionView] Compression not supported, using original image');
        profileUsed = 'compression-not-supported';
        fileToUpload = new File([blob], filename, { 
          type: cameraFile.type 
        });
        setUploadProgress(40);
      }
      
      // Upload to server
      console.log('[SessionView] Uploading camera image:', {
        filename: fileToUpload.name,
        size: formatFileSize(fileToUpload.size),
        type: fileToUpload.type
      });
      
      setUploadProgress(50); // Starting upload
      
      // Measure upload time by wrapping the API call
      const uploadStartTime = performance.now();
      let uploadTime = 0;
      let uploadedScreenshot;
      
      try {
        // Include camera and compression info in metadata
        const captureMetadata = {
          captureMethod: 'camera',
          compressionProfile: profileUsed,
          compressionTime: Math.round(compressionTime),
          originalFileType: cameraFile.type.includes('jpeg') ? 'jpeg' : cameraFile.type.split('/')[1],
          compressedFileType: fileToUpload.type.includes('jpeg') ? 'jpeg' : fileToUpload.type.split('/')[1],
        };
        
        uploadedScreenshot = await uploadScreenshot(
          fileToUpload, 
          sessionId,
          undefined, // positionX
          undefined, // positionY
          undefined, // game
          undefined, // map
          captureMetadata  // metadata with camera and profile info
        );
        uploadTime = performance.now() - uploadStartTime;
        
        // Now update the screenshot with the actual upload time
        await updateScreenshotMetadata(uploadedScreenshot.id, {
          uploadTimeMs: Math.round(uploadTime)
        });
      } catch (uploadError) {
        uploadTime = performance.now() - uploadStartTime;
        throw uploadError; // Re-throw to be caught by outer try-catch
      }
      
      setUploadProgress(80); // Upload complete

      // Notify other users via socket
      if (socketService.isConnected()) {
        socketService.emitScreenshotCaptured(sessionId, uploadedScreenshot.id);
      }

      // Reload screenshots
      await loadScreenshots();
      setUploadProgress(100); // All done
      
      const totalTime = performance.now() - captureStartTime;
      
      // Log comprehensive performance metrics
      console.log('[SessionView] Camera upload complete:', {
        screenshotId: uploadedScreenshot.id,
        captureTimestamp,
        profileUsed,
        performanceMetrics: {
          compressionTime: `${compressionTime.toFixed(1)}ms`,
          uploadTime: `${uploadTime.toFixed(1)}ms`,
          totalTime: `${totalTime.toFixed(1)}ms`,
        },
        fileSize: formatFileSize(fileToUpload.size),
      });
      
      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (err) {
      console.error('[SessionView] Failed to upload camera image:', err);
      if (err.message !== 'Camera capture cancelled') {
        setError('Failed to upload camera image');
      }
      setUploadProgress(0);
    }
  };

  const handleEndSession = async () => {
    if (!window.confirm('Are you sure you want to end this session?')) {
      return;
    }

    try {
      await endSession(sessionId);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to end session');
    }
  };

  const handleDeleteSession = async () => {
    const confirmMessage = `Are you sure you want to delete "${session?.name}"?\n\nThe session will be marked as deleted but data will be preserved.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteSession(sessionId);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete session');
    }
  };

  if (loading) {
    return (
      <div className="session-view loading">
        <div className="loading-spinner"></div>
        <p>Loading session...</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="session-view">
        <p>Session not found</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="session-view">
      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          user={user}
          onLogout={() => navigate('/')}
        />
      )}

      <header className="session-header">
        <div className="header-left">
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            ← Back
          </button>
          <div className="session-info">
            <h1>
              {session.name || 'Unnamed Session'}
              {session.isDeleted && <span className="deleted-badge">🗑️ Deleted</span>}
            </h1>
            {!isMobile && (
              <p className="text-muted">
                {session.squadName} • {session.status}
              </p>
            )}
          </div>
        </div>
        <div className="header-right">
          {isMobile ? (
            <button 
              className="btn-hamburger" 
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
          ) : (
            <>
              <button className="btn btn-primary" onClick={() => navigate(`/fps/${sessionId}`)}>
                🎮 View FPS
              </button>
              <button className="btn btn-primary" onClick={() => navigate(`/map/${sessionId}`)}>
                📍 View Map
              </button>
              {session.status === 'active' && (
                <button className="btn btn-danger" onClick={handleEndSession}>
                  End Session
                </button>
              )}
              {(session.createdBy === user.id || session.isOwner) && !session.isDeleted && (
                <button className="btn btn-danger" onClick={handleDeleteSession}>
                  🗑️ Delete
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Mobile Action Bar - View FPS/Map buttons */}
      {isMobile && (
        <div className="mobile-action-bar">
          <button className="btn btn-primary" onClick={() => navigate(`/fps/${sessionId}`)}>
            🎮 View FPS
          </button>
          <button className="btn btn-primary" onClick={() => navigate(`/map/${sessionId}`)}>
            📍 View Map
          </button>
        </div>
      )}

      <main className="session-content">
        {/* Upload Progress Indicator */}
        {uploadProgress > 0 && (
          <div className="upload-progress-container">
            <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}>
              <span className="upload-progress-text">
                {uploadProgress < 30 && 'Preparing...'}
                {uploadProgress >= 30 && uploadProgress < 50 && 'Compressing...'}
                {uploadProgress >= 50 && uploadProgress < 90 && 'Uploading...'}
                {uploadProgress >= 90 && 'Complete!'}
              </span>
            </div>
          </div>
        )}

        <div className="screenshots-section">
          {!isMobile && (
            <div className="screenshots-header">
              <h2>Screenshots ({filteredScreenshots.length})</h2>
              
              {/* Toolbar */}
              <div className="screenshots-toolbar">
                {/* User filter dropdown */}
                <div className="toolbar-item">
                  <label htmlFor="user-filter">Show:</label>
                  <select 
                    id="user-filter"
                    value={userFilter} 
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="user-filter-dropdown"
                  >
                    <option value="all">All Members</option>
                    <option value="me">My Screenshots</option>
                    {getParticipants().map(participant => (
                      <option key={participant.id} value={participant.id}>
                        {participant.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Sort dropdown */}
                <div className="toolbar-item">
                  <label htmlFor="sort-order">Sort:</label>
                  <select 
                    id="sort-order"
                    value={sortOrder} 
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="sort-dropdown"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>

                {/* Selection controls */}
                {sortedScreenshots.length > 0 && (
                  <>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setIsSelectionMode(!isSelectionMode)}
                    >
                      {isSelectionMode ? '✓ Selection Mode' : '☐ Select'}
                    </button>

                    {isSelectionMode && (
                      <>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleSelectAll}
                        >
                          {selectedScreenshots.size === sortedScreenshots.length ? '☐ Deselect All' : '☑ Select All'}
                        </button>

                        {selectedScreenshots.size > 0 && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={handleDeleteSelected}
                          >
                            🗑️ Delete ({selectedScreenshots.size})
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {filteredScreenshots.length === 0 ? (
            <div className="empty-state">
              <p>
                {screenshots.length === 0 
                  ? 'No screenshots yet. Press F9 to capture your first screenshot!' 
                  : 'No screenshots match the selected filter.'}
              </p>
            </div>
          ) : (
            <div className="screenshots-grid">
              {filteredScreenshots.map((screenshot) => (
                <div 
                  key={screenshot.id} 
                  className={`screenshot-card card ${selectedScreenshots.has(screenshot.id) ? 'selected' : ''}`}
                  onClick={() => {
                    if (isSelectionMode) {
                      handleSelectScreenshot(screenshot.id);
                    } else {
                      handleScreenshotClick(screenshot.id);
                    }
                  }}
                  style={{ cursor: isSelectionMode ? 'pointer' : 'zoom-in' }}
                >
                  {/* Selection checkbox */}
                  {isSelectionMode && (
                    <div className="screenshot-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedScreenshots.has(screenshot.id)}
                        onChange={() => handleSelectScreenshot(screenshot.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}

                  <img
                    src={getImageUrl(screenshot.filePath)}
                    alt={`Screenshot by ${screenshot.username}`}
                    className="screenshot-image"
                    onError={(e) => {
                      console.error('Failed to load screenshot:', screenshot.filePath, getImageUrl(screenshot.filePath));
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="150"%3E%3Crect fill="%231a2332" width="200" height="150"/%3E%3Ctext fill="%23e1e8ed" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImage failed to load%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <div className="screenshot-info">
                    <div>
                      <strong>{screenshot.displayName || screenshot.username}</strong>
                      <p className="text-muted">
                        {new Date(screenshot.capturedAt).toLocaleString()}
                      </p>
                      <p className={`upload-time ${getUploadTimeClass(screenshot.uploadTimeMs)}`}>
                        {formatUploadTime(screenshot.uploadTimeMs)}
                      </p>
                      {getProfileDisplay(screenshot) && (
                        <p className="compression-profile">
                          🎯 {getProfileDisplay(screenshot)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="error-message mt-3">{error}</div>}
      </main>

      {/* Lightbox for full-screen image viewing */}
      {lightboxOpen && (
        <ScreenshotLightbox
          screenshots={filteredScreenshots}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
        />
      )}

      {/* Floating Action Button (Mobile Only) */}
      {isMobile && (
        <FloatingActionButton
          visible={true}
          uploadProgress={uploadProgress}
          onCapture={handleCameraCapture}
        />
      )}
    </div>
  );
}

export default SessionView;

